/**
 * Direction C — "The Rail", gallery side. room-web only.
 *
 * The producer's rundown: the room's card in running order, with what's on now and what's
 * next always resolved. It is the one element that never scrolls away, and it is what
 * orients the room to the clock — the mirror of the player's ticker.
 *
 * Rendered in two slots, because the two need different places in the DOM:
 *
 *   slot="bar"   above the content. A single sticky line on a phone; a horizontal,
 *                scrollable strip on a tablet, which is the floor device.
 *   slot="rail"  beside the content, from lg up. The full vertical running order — 236px, 300px from
 *                1440px, or a slim handle when hidden (#575). Below lg the bar's switch opens it as
 *                an overlay over the content instead.
 *
 * Rule 2 is enforced here rather than documented: a segment renders in signal orange only
 * when it carries an `action`. A tournament that is merely live is not work waiting on you,
 * so it gets the live marker and the charcoal treatment instead. That keeps the promise that
 * orange in this app always has a control under it.
 *
 * `control` is the caller's own control over what the rundown covers (room-web's Rundown period,
 * docs/specs/user-preferences.md UP23): drawn under the rail's heading and at the start of the tablet strip.
 * The phone's one line has no room for it; the period chosen still applies there.
 */

import type { ReactNode } from 'react';
import { railClass, railContentClass, railHandleClass, switchLabel, type RailState } from './rundownRail';

/**
 * The rail's hide/show state and its one switch (#575). From lg the switch in the rail's header hides the column to a
 * slim handle, and the handle shows it again; below lg the switch in the bar opens the rail as an overlay. Every switch
 * is a real button naming the running order it controls. The caller decides what a press does at the current width
 * (`toggleRail`) and remembers the choice; this only draws it. Chalk, never orange: hiding a list is not work.
 */
export interface RundownRailControl {
  state: RailState;
  onToggle: () => void;
  /** The id of the rail's running order, which every switch names in `aria-controls`. */
  contentId: string;
}

export type RundownState = 'now' | 'next' | 'upcoming' | 'past';

export interface RundownSegment {
  id: string;
  /**
   * Where this sits in the running order, already formatted by the caller — a wall-clock
   * time where the API carries one, otherwise a day label such as "Today" or "Sat 25".
   */
  when: string;
  name: string;
  detail?: string;
  state: RundownState;
  /**
   * Work waiting on the operator. Supplying this is the only way a segment turns orange;
   * the label becomes the control.
   */
  action?: { label: string; onSelect: () => void };
}

const STATE_LABEL: Record<RundownState, string> = {
  now: 'Now',
  next: 'Next',
  upcoming: '',
  past: '',
};

export function Rundown({
  date,
  segments,
  slot = 'rail',
  className = '',
  control,
  place,
  rail,
}: {
  /** The span this rundown covers, formatted by the caller — e.g. "Sun 26 Jul". */
  date: string;
  segments: RundownSegment[];
  slot?: 'bar' | 'rail';
  className?: string;
  /** A control over what the rundown covers, never orange: choosing a period is not work waiting on you. */
  control?: ReactNode;
  /**
   * Where this is — room-web passes the room's name. The rail heads with it (two lines at most, then truncated, the
   * full name on hover) and the tablet strip puts it in its first cell; the phone's one line has no room for it. Chalk,
   * never orange: where you are is a statement, not work waiting on you.
   */
  place?: string;
  /** The rail's hide/show state and switch (#575), given to both slots: the bar's switch opens the rail below lg. */
  rail: RundownRailControl;
}) {
  if (slot === 'bar') return <RundownBar segments={segments} className={className} control={control} place={place} rail={rail} />;
  return <RundownRail date={date} segments={segments} className={className} control={control} place={place} rail={rail} />;
}

/** The switch's arrow: toward where the rail goes when pressed. */
function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 16 16" className="size-4 shrink-0" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d={dir === 'left' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'} />
    </svg>
  );
}

function RundownBar({
  segments,
  className,
  control,
  place,
  rail,
}: {
  segments: RundownSegment[];
  className: string;
  control?: ReactNode;
  place?: string;
  rail: RundownRailControl;
}) {
  const now = segments.find((s) => s.state === 'now');
  const next = segments.find((s) => s.state === 'next');
  const open = rail.state.overlayOpen;

  return (
    <div className={`lg:hidden ${className}`}>
      {/* Phone — one line, because nothing else fits. */}
      <div className="flex items-center gap-2 overflow-hidden bg-rail-900 px-3.5 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-white md:hidden">
        {/* The caller's control leads the line, so whatever bounds the rundown is said on a phone too (room-web's
            Rundown period, known gap 148). It must be compact: it shares the line with Now and Next. */}
        {control ? <span className="shrink-0 border-r border-white/10 pr-2">{control}</span> : null}
        {now ? (
          <>
            <span className={`shrink-0 ${now.action ? 'text-signal' : 'text-good'}`}>
              <span className="rail-pulse mr-1.5 inline-block size-2 bg-current align-middle" />
              Now
            </span>
            <span title={now.name} className="truncate">{now.name}</span>
          </>
        ) : (
          <span className="shrink-0 text-rail-300">Nothing running</span>
        )}
        {next ? (
          <span className="ml-auto shrink-0 whitespace-nowrap text-rail-300">
            Next {next.when}
          </span>
        ) : null}
        {/* The whole running order, over the content. Last, at the line's end, as tall as the line. */}
        <button
          type="button"
          data-rundown-switch="bar"
          onClick={rail.onToggle}
          aria-expanded={open}
          aria-controls={rail.contentId}
          aria-label={switchLabel(open)}
          title={switchLabel(open)}
          className={`${next ? '' : 'ml-auto'} -my-3 -mr-2 flex h-10 w-10 shrink-0 items-center justify-center text-rail-300 transition hover:text-white focus-visible:-outline-offset-2`}
        >
          <Chevron dir={open ? 'left' : 'right'} />
        </button>
      </div>

      {/* Tablet — the floor device. Horizontal, scrollable, 44px touch targets. */}
      <div className="hidden overflow-x-auto border-t border-white/10 bg-rail-900 md:flex">
        {place ? (
          <div className="flex min-h-[44px] max-w-[12rem] shrink-0 items-center border-r border-white/10 px-3 py-1.5">
            <span title={place} className="truncate text-[11px] font-bold uppercase tracking-[0.1em] text-rail-100">{place}</span>
          </div>
        ) : null}
        {/* After the room, before the period: the strip scrolls sideways, so the switch must not be at its far end. */}
        <button
          type="button"
          data-rundown-switch="bar"
          onClick={rail.onToggle}
          aria-expanded={open}
          aria-controls={rail.contentId}
          aria-label={switchLabel(open)}
          title={switchLabel(open)}
          className="flex min-h-[44px] shrink-0 items-center gap-1.5 border-r border-white/10 px-3 text-[10px] font-bold uppercase tracking-[0.1em] text-rail-300 transition hover:text-white focus-visible:-outline-offset-2"
        >
          Rundown
          <Chevron dir={open ? 'left' : 'right'} />
        </button>
        {control ? (
          <div className="flex min-h-[44px] shrink-0 items-center border-r border-white/10 px-3 py-1.5">{control}</div>
        ) : null}
        {segments.map((seg) => (
          <StripCell key={seg.id} segment={seg} />
        ))}
      </div>
    </div>
  );
}

function RundownRail({
  date,
  segments,
  className,
  control,
  place,
  rail,
}: {
  date: string;
  segments: RundownSegment[];
  className: string;
  control?: ReactNode;
  place?: string;
  rail: RundownRailControl;
}) {
  const pending = segments.filter((s) => s.action).length;
  const { state, onToggle, contentId } = rail;

  return (
    <aside data-rundown-rail className={`${railClass(state)} pb-4 ${className}`} aria-label={`Running order, ${date}`}>
      {/* Hidden from lg: the slim handle, never gone. Work awaiting results is still counted on it. */}
      <button
        type="button"
        data-rundown-switch="handle"
        onClick={onToggle}
        aria-expanded={false}
        aria-controls={contentId}
        aria-label={switchLabel(false)}
        title={pending > 0 ? `${switchLabel(false)} · ${pending} awaiting results` : switchLabel(false)}
        className={`${railHandleClass(state)} w-full flex-1 flex-col items-center gap-3 pt-3.5 text-rail-300 transition hover:text-white focus-visible:-outline-offset-2`}
      >
        <Chevron dir="right" />
        {/* Bottom-to-top, the left-edge convention: vertical-rl turned half a turn (sideways-lr is not in every browser). */}
        <span className="rotate-180 text-[10px] font-bold uppercase tracking-[0.18em] [writing-mode:vertical-rl]">Rundown</span>
        {pending > 0 ? <span className="num text-[13px] font-bold text-signal">{pending}</span> : null}
      </button>

      <div id={contentId} className={railContentClass(state)}>
        <div className="flex items-start gap-2 px-4 pt-3.5">
          {place ? (
            <p title={place} className="line-clamp-2 min-w-0 flex-1 text-[12px] font-bold uppercase leading-snug tracking-[0.1em] text-rail-100">{place}</p>
          ) : (
            <span className="flex-1" />
          )}
          <button
            type="button"
            data-rundown-switch="rail"
            onClick={onToggle}
            aria-expanded={true}
            aria-controls={contentId}
            aria-label={switchLabel(true)}
            title={switchLabel(true)}
            className="-mr-2 -mt-1.5 flex size-8 shrink-0 items-center justify-center text-rail-300 transition hover:text-white focus-visible:-outline-offset-2"
          >
            <Chevron dir="left" />
          </button>
        </div>
        <h2 className="flex items-baseline justify-between gap-2 px-4 pb-2.5 pt-1 text-[10px] font-bold uppercase tracking-[0.18em] text-rail-300">
          <span>Rundown</span>
          <span className="truncate font-semibold tracking-[0.1em]">{date}</span>
        </h2>

        {control ? <div className="px-4 pb-3">{control}</div> : null}

        {segments.length === 0 ? (
          <p className="px-4 py-3 text-[11px] text-rail-300">Nothing scheduled.</p>
        ) : null}

        {segments.map((seg, i) => {
          const startsPast = seg.state === 'past' && segments[i - 1]?.state !== 'past';
          return (
            <div key={seg.id}>
              {startsPast ? (
                <div className="px-4 pb-1 pt-3 text-[9.5px] font-bold uppercase tracking-[0.18em] text-rail-300/70">
                  Already played
                </div>
              ) : null}
              <RailSegment segment={seg} />
            </div>
          );
        })}

        {pending > 0 ? (
          <div className="mx-4 mt-3.5 border-t border-white/10 pt-3 text-[11px] leading-snug text-rail-300">
            <span className="num block text-[15px] font-bold leading-none text-signal">{pending}</span>
            {pending === 1 ? 'result still needs uploading' : 'results still need uploading'}
          </div>
        ) : null}
      </div>
    </aside>
  );
}

function RailSegment({ segment }: { segment: RundownSegment }) {
  const urgent = !!segment.action;
  const label = STATE_LABEL[segment.state];

  return (
    <div
      className={
        urgent
          ? 'border-t border-signal bg-signal px-4 py-2.5 text-white'
          : 'border-t border-white/10 px-4 py-2.5'
      }
    >
      <div
        className={`num text-[10.5px] font-bold uppercase tracking-[0.14em] ${
          urgent ? 'text-white/80' : segment.state === 'past' ? 'text-rail-300/60' : 'text-rail-300'
        }`}
      >
        {segment.state === 'now' && !urgent ? (
          <span className="rail-pulse mr-1.5 inline-block size-2 bg-good align-middle" />
        ) : null}
        {label ? `${label} · ` : ''}
        {segment.when}
      </div>

      <div
        className={`mt-1 text-[13px] font-bold uppercase leading-tight tracking-[0.02em] ${
          urgent ? 'text-white' : segment.state === 'past' ? 'text-rail-300/70' : 'text-white/90'
        }`}
      >
        {segment.name}
      </div>

      {segment.detail ? (
        <div className={`num mt-1 text-[11px] ${urgent ? 'text-white/80' : 'text-rail-300'}`}>
          {segment.detail}
        </div>
      ) : null}

      {segment.action ? (
        <button
          type="button"
          onClick={segment.action.onSelect}
          className="mt-2 bg-white px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-signal transition hover:bg-white/90"
        >
          {segment.action.label}
        </button>
      ) : null}
    </div>
  );
}

function StripCell({ segment }: { segment: RundownSegment }) {
  const urgent = !!segment.action;
  const label = STATE_LABEL[segment.state];

  return (
    <div
      className={`flex min-h-[44px] min-w-[190px] shrink-0 flex-col justify-center border-r border-white/10 px-4 py-2.5 ${
        urgent ? 'bg-signal' : ''
      }`}
    >
      <div
        className={`num text-[9.5px] font-bold uppercase tracking-[0.16em] ${
          urgent ? 'text-white/80' : 'text-rail-300'
        }`}
      >
        {segment.state === 'now' && !urgent ? (
          <span className="rail-pulse mr-1.5 inline-block size-2 bg-good align-middle" />
        ) : null}
        {label ? `${label} · ` : ''}
        {segment.when}
      </div>
      <div
        title={segment.name}
        className={`mt-1 truncate text-[13px] font-bold uppercase leading-tight ${
          urgent ? 'text-white' : 'text-white/90'
        }`}
      >
        {segment.name}
      </div>
      {segment.action ? (
        <button
          type="button"
          onClick={segment.action.onSelect}
          className="mt-1.5 self-start bg-white px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-signal"
        >
          {segment.action.label}
        </button>
      ) : segment.detail ? (
        <div className={`num mt-0.5 text-[10.5px] ${urgent ? 'text-white/80' : 'text-rail-300'}`}>
          {segment.detail}
        </div>
      ) : null}
    </div>
  );
}
